import { useEffect, useRef, useState } from "react";
import { ChevronsRight, Delete, LogOut } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";

const Clock = () => {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);
  return (
    <div className="text-center" data-testid="lock-clock">
      <p className="font-heading text-6xl sm:text-7xl font-bold tracking-tight">{now.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" })}</p>
      <p className="mt-2 text-sm opacity-80">{now.toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</p>
    </div>
  );
};

const Slider = ({ onDone }) => {
  const track = useRef(null);
  const start = useRef(null);
  const [x, setX] = useState(0);
  const max = () => (track.current ? track.current.offsetWidth - 56 : 200);
  const down = (e) => { e.currentTarget.setPointerCapture(e.pointerId); start.current = e.clientX - x; };
  const move = (e) => { if (start.current !== null) setX(Math.max(0, Math.min(max(), e.clientX - start.current))); };
  const up = () => { start.current = null; if (x > max() * 0.85) onDone(); else setX(0); };
  return (
    <div ref={track} className="relative h-16 w-full max-w-xs rounded-full bg-white/15 backdrop-blur border border-white/20 select-none" data-testid="lock-slider">
      <span className="absolute inset-0 grid place-items-center text-sm font-medium opacity-80 pointer-events-none">Geser ke kanan untuk membuka</span>
      <button type="button" onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up} style={{ transform: `translateX(${x}px)`, transition: start.current === null ? "transform .25s" : "none" }}
        className="absolute top-1 left-1 h-14 w-14 rounded-full bg-white text-neutral-900 grid place-items-center shadow-lg touch-none cursor-grab" aria-label="Geser untuk membuka" data-testid="lock-slider-handle">
        <ChevronsRight className="h-6 w-6" />
      </button>
    </div>
  );
};

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "", "0", "del"];

const PinPad = ({ onUnlock, onLogout }) => {
  const [pin, setPin] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const press = (k) => {
    if (busy) return;
    if (k === "del") { setPin((p) => p.slice(0, -1)); return; }
    const next = (pin + k).slice(0, 6);
    setPin(next); setErr("");
    if (next.length === 6) {
      setBusy(true);
      api.post("/auth/pin/verify", { pin: next }).then(onUnlock).catch((e) => {
        if (e.response?.status === 423) { onLogout(); return; }
        setErr(errMsg(e)); setPin("");
      }).finally(() => setBusy(false));
    }
  };
  return (
    <div className="w-full max-w-xs" data-testid="lock-pinpad">
      <p className="text-center text-sm mb-4">Masukkan PIN</p>
      <div className="flex justify-center gap-3 mb-3">{[0, 1, 2, 3, 4, 5].map((i) => <span key={i} className={`h-3.5 w-3.5 rounded-full border-2 border-white ${i < pin.length ? "bg-white" : ""}`} />)}</div>
      <p className="h-5 text-center text-xs text-rose-200" data-testid="lock-pin-error">{err}</p>
      <div className="grid grid-cols-3 gap-3 mt-2">
        {KEYS.map((k, i) => k === "" ? <span key={i} /> : (
          <button key={i} type="button" onClick={() => press(k)} className="h-16 rounded-full bg-white/15 hover:bg-white/25 active:scale-95 transition-transform text-2xl font-semibold grid place-items-center" data-testid={`lock-key-${k}`}>
            {k === "del" ? <Delete className="h-6 w-6" /> : k}
          </button>
        ))}
      </div>
    </div>
  );
};

export const LockScreen = ({ onUnlock, onLogout }) => {
  const { user } = useAuth();
  const { settings } = useSettings();
  const [step, setStep] = useState("slide");
  return (
    <div className="fixed inset-0 z-[200] bg-primary text-primary-foreground flex flex-col items-center justify-between py-14 px-6" data-testid="lock-screen">
      <div className="text-center">
        <p className="text-xs uppercase tracking-[0.2em] opacity-75">{settings.app_name}</p>
        <p className="mt-1 font-semibold" data-testid="lock-user-name">{user.name}</p>
      </div>
      <Clock />
      {step === "slide" ? <Slider onDone={() => setStep("pin")} /> : <PinPad onUnlock={onUnlock} onLogout={onLogout} />}
      <button type="button" onClick={onLogout} className="inline-flex items-center gap-1.5 text-sm opacity-80 hover:opacity-100" data-testid="lock-logout"><LogOut className="h-4 w-4" />Keluar</button>
    </div>
  );
};

export const useIdleLock = (user) => {
  const [locked, setLocked] = useState(() => {
    if (sessionStorage.getItem("ksp_fresh_login")) return false;
    return Boolean(user.lock_enabled);
  });
  // consume the fresh-login flag in an effect (StrictMode double-invokes state initializers in dev)
  useEffect(() => { sessionStorage.removeItem("ksp_fresh_login"); }, []);
  const last = useRef(Date.now());
  useEffect(() => {
    if (!user.lock_enabled) { setLocked(false); return undefined; }
    const limit = user.lock_idle * 60000;
    const touch = () => { last.current = Date.now(); };
    const check = () => { if (Date.now() - last.current > limit) setLocked(true); };
    const onVis = () => { if (!document.hidden) check(); };
    const evs = ["pointerdown", "keydown", "scroll", "touchstart"];
    evs.forEach((e) => window.addEventListener(e, touch, { passive: true }));
    document.addEventListener("visibilitychange", onVis);
    const t = setInterval(check, 10000);
    return () => { evs.forEach((e) => window.removeEventListener(e, touch)); document.removeEventListener("visibilitychange", onVis); clearInterval(t); };
  }, [user.lock_enabled, user.lock_idle]);
  const unlock = () => { last.current = Date.now(); setLocked(false); };
  return [locked, unlock];
};
